import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  MdPerson,
  MdWork,
  MdRefresh,
  MdWarning,
  MdTrendingUp,
  MdTrendingDown,
  MdChatBubbleOutline,
  MdSend,
} from "react-icons/md";
import Card, { CardHeader } from "components/card";
import Badge from "components/badge/Badge";
import Avatar from "components/avatar";
import Button from "components/button";
import Skeleton from "components/skeleton/Skeleton";
import Chart from "components/charts/Chart";
import { PageContainer, PageHeader } from "components/page";
import { EmptyState, ErrorState } from "components/states";
import { getAudience } from "components/assessment/audiences";
import { archetypeDescriptions } from "data/archetypeDescriptions";
import { getArchetype } from "lib/archetypes";
import { palette } from "lib/chartTheme";
import notify from "lib/notify";
import { track } from "lib/mixpanel";
import { getApiErrorMessage } from "api";
import { formatDate } from "utils/format";

const getSentiment = (label) => {
  if (label === "positive" || label === "very_positive") return { tone: "success", icon: MdTrendingUp };
  if (label === "negative" || label === "very_negative") return { tone: "danger", icon: MdTrendingDown };
  return { tone: "warning", icon: MdChatBubbleOutline };
};

const scoreTone = (score) => {
  if (score >= 4) return "success";
  if (score >= 3) return "warning";
  return "danger";
};

const SCORE_TEXT = {
  success: "text-success-fg",
  warning: "text-warning-fg",
  danger: "text-danger-fg",
};

const fitTone = (score) => {
  if (score >= 80) return "success";
  if (score >= 60) return "warning";
  return "danger";
};

const FIT_BAR = {
  success: "bg-success",
  warning: "bg-warning",
  danger: "bg-danger",
};

// Labels for old-format response questionIds (before the pulse questions migration)
const OLD_QUESTION_LABELS = {
  energy: "Energy Level",
  workload: "Workload",
  fulfillment: "Fulfillment",
  support: "Support Received",
};

const InsightCard = ({ title, items, icon: Icon, tone = "default" }) => {
  if (!items || items.length === 0) return null;
  const danger = tone === "danger";
  return (
    <Card padding="md">
      <CardHeader
        title={
          <span className="flex items-center gap-2">
            {Icon && <Icon aria-hidden="true" className={`h-5 w-5 ${danger ? "text-danger-fg" : "text-primary"}`} />}
            {title}
          </span>
        }
      />
      <ul className="mt-3 space-y-2">
        {items.map((item, idx) => (
          <li key={idx} className="flex items-start gap-2.5 text-sm leading-relaxed text-fg-muted">
            <span
              aria-hidden="true"
              className={`mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full ${danger ? "bg-danger" : "bg-accent"}`}
            />
            {item}
          </li>
        ))}
      </ul>
    </Card>
  );
};

const ProfileSkeleton = () => (
  <>
    <Skeleton className="h-24 w-full rounded-card" />
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-3">
      {Array.from({ length: 6 }, (_, i) => (
        <Skeleton key={i} className="h-40 w-full rounded-card" />
      ))}
    </div>
    <Skeleton className="h-64 w-full rounded-card" />
  </>
);

const CheckInCard = ({ checkin, questionTextMap }) => {
  const responses = checkin.responses || [];
  const sentiment = getSentiment(checkin.sentimentLabel);
  const SentimentIcon = sentiment.icon;

  // Separate text answers from rating answers
  const textResponses = responses.filter((r) => typeof r.value === "string" && r.value.length > 0);
  const ratingResponses = responses.filter((r) => typeof r.value === "number");

  return (
    <Card className="overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-line px-5 py-3">
        <div className="flex items-center gap-3">
          <Badge tone={sentiment.tone} className="capitalize">
            <SentimentIcon aria-hidden="true" className="h-3.5 w-3.5" />
            {(checkin.sentimentLabel || "unknown").replace("_", " ")}
          </Badge>
          <span className="text-sm font-medium text-fg-muted">{formatDate(checkin.submittedAt)}</span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {ratingResponses.map((r, idx) => {
            const label = r.question || questionTextMap[r.questionId] || OLD_QUESTION_LABELS[r.questionId] || "";
            const shortLabel = OLD_QUESTION_LABELS[r.questionId]
              ? OLD_QUESTION_LABELS[r.questionId].split(" ")[0]
              : label.includes("role")
                ? "Role"
                : label.includes("contribut")
                  ? "Contrib."
                  : `Q${idx + 1}`;
            return (
              <Badge key={idx} tone={scoreTone(r.value)} title={label || undefined}>
                <span className="text-2xs uppercase tracking-wider">{shortLabel}</span>
                <span className="font-bold">{r.value}/5</span>
              </Badge>
            );
          })}
          {checkin.overallScore != null && (
            <Badge tone={scoreTone(checkin.overallScore)}>
              <span className="text-2xs uppercase tracking-wider">Avg</span>
              <span className="font-bold">{checkin.overallScore}</span>
            </Badge>
          )}
        </div>
      </div>

      <div className="px-5 py-4">
        {textResponses.length > 0 ? (
          <div className="space-y-4">
            {textResponses.map((r, idx) => {
              const questionText = r.question || questionTextMap[r.questionId] || `Question ${idx + 1}`;
              const isBlocker = questionText.toLowerCase().includes("block");
              const hasContent = r.value && r.value.toLowerCase() !== "none" && r.value.trim().length > 0;

              return (
                <div key={idx}>
                  <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-fg-muted">{questionText}</p>
                  {hasContent ? (
                    <p
                      className={`text-sm leading-relaxed ${
                        isBlocker
                          ? "rounded-control border border-warning/20 bg-warning-subtle p-3 text-warning-fg"
                          : "text-fg"
                      }`}
                    >
                      {isBlocker && <MdWarning aria-hidden="true" className="mr-1.5 inline h-4 w-4" />}
                      {r.value}
                    </p>
                  ) : (
                    <p className="text-sm italic text-fg-muted">No answer provided</p>
                  )}
                </div>
              );
            })}
          </div>
        ) : ratingResponses.length > 0 ? (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {ratingResponses.map((r, idx) => {
              const label =
                r.question || questionTextMap[r.questionId] || OLD_QUESTION_LABELS[r.questionId] || `Rating ${idx + 1}`;
              return (
                <div key={idx} className="rounded-control bg-surface-subtle p-3 text-center">
                  <p className="mb-1 text-2xs font-semibold uppercase tracking-wider text-fg-muted">{label}</p>
                  <p className={`text-xl font-bold ${SCORE_TEXT[scoreTone(r.value)]}`}>
                    {r.value}
                    <span className="text-xs font-normal text-fg-muted">/5</span>
                  </p>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-sm italic text-fg-muted">No detailed responses recorded for this check-in</p>
        )}

        {checkin.aiSummary && (
          <div className="mt-4 rounded-control border border-info/20 bg-info-subtle p-3 text-info-fg">
            <p className="mb-1 text-xs font-semibold uppercase tracking-wider">AI Insight</p>
            <p className="text-sm leading-relaxed">{checkin.aiSummary}</p>
          </div>
        )}
      </div>
    </Card>
  );
};

export default function ProfileDetails({ audience }) {
  const config = getAudience(audience);
  const { labels } = config;
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [user, setUser] = useState(null);
  const [hasProfile, setHasProfile] = useState(false);
  const [profile, setProfile] = useState(null);
  const [pulseHistory, setPulseHistory] = useState([]);
  const [pulseQuestions, setPulseQuestions] = useState([]);
  const [roleMatches, setRoleMatches] = useState([]);
  const [regenerating, setRegenerating] = useState(false);

  const peoplePath = `${config.basePath}/people`;

  const fetchProfile = async () => {
    try {
      setLoading(true);
      setError(null);
      const [response, questionsRes] = await Promise.all([
        config.service.getUserProfile(id),
        config.service.getPulseQuestions(),
      ]);
      if (response.success) {
        setUser(response.data.user);
        setHasProfile(response.data.hasProfile);
        setProfile(response.data.profile);
        setPulseHistory(response.data.pulseHistory || []);
        if (response.data.roleMatches?.length > 0) {
          setRoleMatches(response.data.roleMatches);
        }
      }
      if (questionsRes.success) {
        setPulseQuestions(questionsRes.data || []);
      }
    } catch (err) {
      // A missing profile falls through to the not-found state below.
      if (err.response?.status !== 404) setError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    track(config.events.profileViewed, { [config.events.profileIdProperty]: id });
    window.scrollTo(0, 0);
    fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleRegenerateSummary = async () => {
    try {
      setRegenerating(true);
      const response = await config.service.regenerateProfileSummary(id);
      if (response.success) {
        notify.success("Profile summary regenerated");
        fetchProfile();
      }
    } catch (err) {
      notify.error(getApiErrorMessage(err, "Could not regenerate summary"));
    } finally {
      setRegenerating(false);
    }
  };

  const sortedDimensions = useMemo(
    () => Object.entries(profile?.dimensionScores || {}).sort((a, b) => b[1] - a[1]),
    [profile]
  );

  const barChartOptions = useMemo(
    () => ({
      plotOptions: { bar: { horizontal: true, borderRadius: 4, barHeight: "60%", distributed: true } },
      dataLabels: { enabled: true, formatter: (val) => `${val}%` },
      legend: { show: false },
      xaxis: {
        categories: sortedDimensions.map(([k]) => getArchetype(k)?.label || k),
        max: 100,
      },
      colors: sortedDimensions.map(([k]) => getArchetype(k)?.chartColor || palette.slate),
      tooltip: { enabled: false },
    }),
    [sortedDimensions]
  );

  const barChartSeries = useMemo(
    () => [{ name: "Score", data: sortedDimensions.map(([, v]) => v) }],
    [sortedDimensions]
  );

  // Question ID -> text map for the pulse history
  const questionTextMap = useMemo(
    () => Object.fromEntries(pulseQuestions.map((q) => [q.id, q.question])),
    [pulseQuestions]
  );

  if (loading) {
    return (
      <PageContainer>
        <PageHeader title="Profile" backTo={peoplePath} backLabel="Back to Profiles" />
        <ProfileSkeleton />
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <PageHeader title="Profile" backTo={peoplePath} backLabel="Back to Profiles" />
        <ErrorState message={getApiErrorMessage(error)} onRetry={fetchProfile} />
      </PageContainer>
    );
  }

  if (!user) {
    return (
      <PageContainer>
        <PageHeader title="Profile Not Found" backTo={peoplePath} backLabel="Back to Profiles" />
        <Card padding="md">
          <EmptyState
            icon={MdPerson}
            title="Profile Not Found"
            description={`We couldn't find a profile for user ID ${id}.`}
            action={
              <Button as={Link} to={peoplePath}>
                Back to Profiles
              </Button>
            }
          />
        </Card>
      </PageContainer>
    );
  }

  const fullName = [user.firstName, user.lastName].filter(Boolean).join(" ");
  const roleLabel = user.role || labels.role;
  const primary = hasProfile ? getArchetype(profile?.primaryStyle) : null;
  const secondary = hasProfile ? getArchetype(profile?.secondaryStyle) : null;
  const PrimaryIcon = (primary && archetypeDescriptions[primary.key]?.icon) || MdPerson;

  // Communication style items
  const commItems = [
    ...(profile?.communicationStyle?.preferred || []).map((s) => `Do: ${s}`),
    ...(profile?.communicationStyle?.avoid || []).map((s) => `Don't: ${s}`),
  ];

  return (
    <PageContainer>
      <PageHeader
        title={fullName}
        leading={<Avatar user={user} size="lg" noBorder decorative />}
        description={user.email}
        backTo={peoplePath}
        backLabel="Back to Profiles"
        actions={
          hasProfile ? (
            <Button
              variant="secondary"
              leftIcon={<MdRefresh aria-hidden="true" />}
              loading={regenerating}
              onClick={handleRegenerateSummary}
            >
              Regenerate
            </Button>
          ) : (
            <Button as={Link} to={`${config.basePath}/settings`} leftIcon={<MdSend aria-hidden="true" />}>
              Send Intake Form
            </Button>
          )
        }
      />

      {!hasProfile && (
        <Card padding="md">
          <div className="flex flex-col items-center gap-4 text-center md:flex-row md:text-left">
            <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-control bg-warning-subtle text-warning-fg">
              <MdWarning aria-hidden="true" className="h-7 w-7" />
            </div>
            <div className="flex-1">
              <h2 className="text-heading">Assessment Not Completed</h2>
              <p className="mt-1 text-sm text-fg-muted">
                {user.firstName} hasn't completed their personality assessment yet. Send them an intake form to get
                started.
              </p>
            </div>
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-line pt-4 text-sm text-fg-muted">
            <span className="flex items-center gap-2">
              <MdWork aria-hidden="true" className="h-4 w-4" />
              <span className="capitalize">{roleLabel}</span>
            </span>
            {user.department && <span>{user.department}</span>}
          </div>
        </Card>
      )}

      {hasProfile && profile && (
        <Card padding="sm">
          <div className="flex items-center gap-4">
            <div
              className={`flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-control ${
                primary ? primary.badgeClass : "bg-surface-subtle text-fg-muted"
              }`}
            >
              <PrimaryIcon aria-hidden="true" className="h-7 w-7" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="text-base font-bold text-fg">{primary?.label || profile.primaryStyle}</h2>
                {secondary && <span className="text-xs text-fg-muted">/ {secondary.label}</span>}
                <span className="flex items-center gap-1 text-xs text-fg-muted">
                  <MdWork aria-hidden="true" className="h-3.5 w-3.5" />
                  <span className="capitalize">{roleLabel}</span>
                </span>
              </div>
              <p className="mt-1 line-clamp-2 text-sm leading-relaxed text-fg-muted">
                {profile.profileSummary || "No profile summary available."}
              </p>
            </div>
          </div>
        </Card>
      )}

      {hasProfile && profile && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-3">
          <InsightCard title="Strengths" items={profile.strengths} />
          <InsightCard title="Growth Areas" items={profile.growthAreas} />
          <InsightCard title="Ideal Roles" items={profile.communicationStyle?.tips} />
          <InsightCard title="Communication Style" items={commItems.length > 0 ? commItems : null} />
          <InsightCard title="Burnout Warnings" items={profile.stressTriggers} icon={MdWarning} tone="danger" />
          <InsightCard title="Manager Playbook" items={profile.motivationFactors} />
        </div>
      )}

      {pulseHistory.length > 0 && (
        <section>
          <h2 className="text-heading mb-4">Weekly Check-In Responses</h2>
          <div className="space-y-4">
            {pulseHistory.map((checkin) => (
              <CheckInCard key={checkin.id} checkin={checkin} questionTextMap={questionTextMap} />
            ))}
          </div>
        </section>
      )}

      {hasProfile && sortedDimensions.length > 0 && (
        <Card padding="md">
          <CardHeader title="Archetype Score Breakdown" />
          <Chart type="bar" height="md" options={barChartOptions} series={barChartSeries} className="mt-4" />
        </Card>
      )}

      {pulseHistory.length === 0 && (
        <Card padding="md">
          <EmptyState
            icon={MdChatBubbleOutline}
            title="No Check-In Responses Yet"
            description={`${user.firstName} hasn't completed any weekly check-ins yet.`}
          />
        </Card>
      )}

      {roleMatches.length > 0 && (
        <Card padding="md">
          <CardHeader title="Role Fit Analysis" />
          <ul className="mt-4 space-y-3">
            {roleMatches.map((match) => {
              const fitScore = match.overallFitScore || match.fitScore || 0;
              const tone = fitTone(fitScore);
              return (
                <li
                  key={match.id}
                  className="flex flex-wrap items-center justify-between gap-3 rounded-control border border-line p-4"
                >
                  <div>
                    <p className="font-medium text-fg">{match.roleProfile?.name || "Unknown Role"}</p>
                    <p className="text-sm text-fg-muted">{match.roleProfile?.ministryArea || "General"}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <div aria-hidden="true" className="h-2 w-32 rounded-full bg-surface-subtle">
                      <div className={`h-2 rounded-full ${FIT_BAR[tone]}`} style={{ width: `${fitScore}%` }} />
                    </div>
                    <span className={`text-lg font-bold ${SCORE_TEXT[tone]}`}>{fitScore}%</span>
                  </div>
                </li>
              );
            })}
          </ul>
        </Card>
      )}
    </PageContainer>
  );
}
